<template>
  <div class="flex flex-col items-center justify-center min-h-screen bg-gray-50">
    <Toast />
    <div class="bg-white p-6 rounded-xl shadow-md w-full max-w-md">
      <h2 class="text-2xl font-semibold text-center mb-4 text-gray-800">
        Forgot Password
      </h2>
      <p class="text-gray-600 text-sm text-center mb-4">
        Enter your registered email and we’ll send you instructions to reset your password.
      </p>

      <form @submit.prevent="handleSubmit" class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Account type</label>
          <SelectButton
            v-model="broker"
            :options="brokerOptions"
            optionLabel="label"
            optionValue="value"
            :allowEmpty="false"
            class="w-full"
            data-testid="forgot-password-broker"
          />
          <small class="text-gray-500 text-xs">
            One address can hold both a staff and a student account, so tell us which to reset.
          </small>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <InputText
            v-model="email"
            placeholder="Enter your email"
            class="w-full"
            type="email"
            :class="{ 'p-invalid': $v.email.$error }"
          />
          <small v-if="$v.email.$error" class="text-red-500 text-xs">Email is required</small>
        </div>

        <Button
          label="Send Reset Link"
          class="bg-primary hover:bg-primary/90 border-none text-white w-full"
          :loading="loading"
          type="submit"
        />
      </form>

      <div class="text-center mt-6">
        <RouterLink to="/login" class="text-sm text-gray-600 hover:text-primary">
          ← Back to Login
        </RouterLink>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useToast } from 'primevue/usetoast';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useMutation } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { FORGOT_PASSWORD } from '@/graphql';
import { PasswordBroker } from '@/types/composition-functions';

const email = ref('');
const loading = ref(false);
const router = useRouter();
const toast = useToast();

// Reset tokens are minted per broker, so the server has to be told which
// account to reset. It cannot be inferred from the address: the same email can
// hold both a staff and a student account.
const broker = ref<PasswordBroker>(PasswordBroker.Users);
const brokerOptions = [
  { label: 'Staff', value: PasswordBroker.Users },
  { label: 'Student', value: PasswordBroker.Students },
];

// ✅ Vuelidate rules
const rules = { email: { required } };
const $v = useVuelidate(rules, { email });

// ✅ Apollo Mutation Hook
const { mutate: forgotPassword } = useMutation(FORGOT_PASSWORD);

// ✅ Handle Submit
const handleSubmit = async() => {
  $v.value.$touch();
  if ($v.value.$invalid) {
    toast.add({
      severity: 'warn',
      summary: 'Validation Error',
      detail: 'Please enter your email address.',
      life: 3000,
    });
    return;
  }

  loading.value = true;

  try {
    const { data } = (await forgotPassword({
      input: { email: email.value, broker: broker.value },
    }))!;
    const response = data?.forgotPassword;

    if (response?.success) {
      // Deliberately a fixed string rather than response.message. The server
      // answers unknown and throttled addresses identically to a real send so
      // the endpoint cannot be used to test who has an account; echoing its
      // wording back would undo that.
      toast.add({
        severity: 'success',
        summary: 'Check your inbox',
        detail: 'If an account exists for that address, we\'ve sent a reset link. '
          + 'It expires in 10 hours — remember to check your spam folder.',
        life: 6000,
      });
      email.value = '';
      $v.value.$reset();
    } else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: response?.message || 'Failed to send reset link.',
        life: 4000,
      });
    }
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Network Error',
      detail: 'Something went wrong. Please try again.',
      life: 4000,
    });
  } finally {
    loading.value = false;
  }
};
</script>
